import { Component, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { RequestDialogProvider } from "./components/RequestDialog";
import ChatWidget from "./components/ChatWidget";
import Footer from "./components/Footer";
import { trackVisit } from "./lib/seo";

import Home from "./pages/Home";
import ServicePage from "./pages/ServicePage";
import { About, Contact, Privacy, Dealers } from "./pages/StaticPages";
import { BlogList, BlogPost } from "./pages/Blog";
import RequestPage from "./pages/RequestPage";
import StaffLogin from "./pages/StaffLogin";
import Operations from "./pages/Operations";
import Intake from "./pages/Intake";
import TicketsList from "./pages/TicketsList";
import TicketDetail from "./pages/TicketDetail";
import { ContentAdmin, BlogAdmin, NotificationsAdmin, AssistantSettings, AnalyticsAdmin } from "./pages/AdminExtras";
import { CustomerAccess, DealerPortal } from "./pages/CustomerPages";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 60, textAlign: "center", color: "var(--ink)" }}>
          <h2 style={{ marginBottom: 12 }}>That section hit a snag.</h2>
          <p className="muted">Reload the page, or call the shop at (562) 425-2027.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const PUBLIC_PATHS = /^\/(services|blog|about|contact|privacy|dealers|appointments|repair-request)?(\/.*)?$/;

function Tracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/staff") || pathname.startsWith("/intake") || pathname.startsWith("/tickets")) return;
    trackVisit(pathname);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Tracker />
        <SmoothScroll />
        <RequestDialogProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/dealers" element={<Dealers />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/appointments" element={<RequestPage appointment />} />
            <Route path="/repair-request" element={<RequestPage />} />
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/admin/operations" element={<Operations />} />
            <Route path="/intake" element={<Intake />} />
            <Route path="/tickets" element={<TicketsList />} />
            <Route path="/tickets/:id" element={<TicketDetail />} />
            <Route path="/admin/blog" element={<BlogAdmin />} />
            <Route path="/admin/analytics" element={<AnalyticsAdmin />} />
            <Route path="/admin/content" element={<ContentAdmin />} />
            <Route path="/admin/notifications" element={<NotificationsAdmin />} />
            <Route path="/assistant-settings" element={<AssistantSettings />} />
            <Route path="/customer" element={<CustomerAccess />} />
            <Route path="/dealer-portal" element={<DealerPortal />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
          <ChatWidget />
        </RequestDialogProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
